import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Save, Eye, Upload, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_HOME, mergeHome, newBlock, sectionLabel } from "@/lib/homeContent";
import { Field, VisibleSwitch, OrderList, Card } from "@/components/admin/CmsFields";
import { BlockEditor, AddBlockBar } from "@/components/admin/CmsBlockEditors";
import { SeoEditor, AdStats } from "@/components/admin/CmsSeoAds";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");
const ACTION_TR = { published: "Yayınlandı", rollback: "Geri dönüldü", reset_default: "Varsayılana dönüldü" };
const PILLAR_TR = { emergency: "Acil yol yardım kartı", transport: "Taşıma yaptır kartı", return: "Boş dönüş kartı" };

const HeroEditor = ({ h, set }) => (
  <Card title="Hero (karşılama alanı)" testid="cms-hero" right={<Badge variant="secondary">Her zaman görünür</Badge>}>
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Rozet metni" value={h.badge_text} max={80} onChange={(v) => set({ badge_text: v })} testid="cms-hero-badge" />
      <Field label="Vurgulu başlık satırı" value={h.title_highlight} max={80} onChange={(v) => set({ title_highlight: v })} testid="cms-hero-highlight" />
    </div>
    <Field label="Başlık (her satır ayrı)" area value={h.title} max={160} onChange={(v) => set({ title: v })} testid="cms-hero-title" />
    <Field label="Açıklama" area value={h.subtitle} max={400} onChange={(v) => set({ subtitle: v })} testid="cms-hero-subtitle" />
    <Field label="Arka plan görseli (https:// adresi, boş = mevcut tasarım)" value={h.background_image_url} max={500} onChange={(v) => set({ background_image_url: v })} testid="cms-hero-image" />
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Buton metni (ops.)" value={h.cta_text} max={40} onChange={(v) => set({ cta_text: v })} testid="cms-hero-cta-text" />
      <Field label="Buton bağlantısı (ops.)" value={h.cta_link} max={300} onChange={(v) => set({ cta_link: v })} testid="cms-hero-cta-link" hint="Örn: /tasima-yaptir veya https://..." />
    </div>
    <p className="text-[11px] text-muted-foreground">Arama kutusu, konum ve il/ilçe seçimi değiştirilemez; her zaman çalışır.</p>
  </Card>
);

const PillarsEditor = ({ p, set }) => {
  const setItem = (key, patch) => set({ items: p.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) });
  return (
    <Card title="Avantaj kartları" testid="cms-pillars" right={<VisibleSwitch checked={p.visible} onChange={(v) => set({ visible: v })} testid="cms-pillars-visible" />}>
      {p.items.map((i) => (
        <div key={i.key} className="rounded-xl bg-muted/30 p-3 space-y-2" data-testid={`cms-pillar-${i.key}`}>
          <div className="flex items-center justify-between"><span className="text-sm font-medium">{PILLAR_TR[i.key]}</span>
            <VisibleSwitch checked={i.visible} onChange={(v) => setItem(i.key, { visible: v })} testid={`cms-pillar-visible-${i.key}`} /></div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Field label="Başlık" value={i.title} max={40} onChange={(v) => setItem(i.key, { title: v })} testid={`cms-pillar-title-${i.key}`} />
            <Field label="Açıklama" value={i.description} max={120} onChange={(v) => setItem(i.key, { description: v })} testid={`cms-pillar-desc-${i.key}`} />
            <Field label="Buton metni" value={i.cta_text} max={30} onChange={(v) => setItem(i.key, { cta_text: v })} testid={`cms-pillar-cta-${i.key}`} />
          </div>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground">Kartların yönlendirmeleri (konum, talep, boş dönüş) ve işletme sahiplerine özel metinler sabittir.</p>
    </Card>
  );
};

const TitledEditor = ({ title, k, s, set, withMax }) => (
  <Card title={title} testid={`cms-${k}`} right={<VisibleSwitch checked={s.visible} onChange={(v) => set({ visible: v })} testid={`cms-${k}-visible`} />}>
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Başlık" value={s.title} max={80} onChange={(v) => set({ title: v })} testid={`cms-${k}-title`} />
      <Field label="Alt açıklama (ops.)" value={s.subtitle} max={200} onChange={(v) => set({ subtitle: v })} testid={`cms-${k}-subtitle`} />
      {withMax && <Field label="Gösterilecek işletme sayısı (1-12)" type="number" value={s.max_items} onChange={(v) => set({ max_items: Math.min(12, Math.max(1, Math.round(v) || 1)) })} testid="cms-featured-max" />}
    </div>
  </Card>
);

const Versions = ({ versions, onRollback }) => (
  <Card title={<span className="flex items-center gap-1"><History className="w-4 h-4" />Yayın geçmişi</span>} testid="cms-versions">
    {!versions.length && <div className="text-sm text-muted-foreground">Henüz yayın yok — ana sayfa varsayılan içerikle çalışıyor.</div>}
    {versions.map((v, idx) => (
      <div key={v.id} className="flex items-center justify-between gap-2 text-sm flex-wrap" data-testid={`cms-version-${v.version}`}>
        <span>v{v.version} · {ACTION_TR[v.action] || v.action}{v.meta?.rollback_of ? ` (v${v.meta.rollback_of})` : ""} · {fmt(v.published_at)} {idx === 0 && <Badge className="ml-1">Yayında</Badge>}</span>
        {idx > 0 && v.action !== "reset_default" && <Button size="sm" variant="outline" onClick={() => onRollback(v.version)} data-testid={`cms-rollback-${v.version}`}><RotateCcw className="w-4 h-4 mr-1" />Bu sürüme dön</Button>}
      </div>
    ))}
  </Card>
);

export const AdminCmsHome = () => {
  const [c, setC] = useState(null);
  const [meta, setMeta] = useState({});
  const [versions, setVersions] = useState([]);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/cms/home").then(({ data }) => { setMeta(data); setC(mergeHome(data.draft || data.published)); setDirty(false); })
      .catch(() => { toast.error("İçerik yüklenemedi, varsayılan gösteriliyor"); setC(DEFAULT_HOME); });
    api.get("/admin/cms/home/versions").then(({ data }) => setVersions(data)).catch(() => setVersions([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!c) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;

  const set = (k) => (patch) => { setC((s) => ({ ...s, [k]: { ...s[k], ...patch } })); setDirty(true); };
  const move = (i, d) => { const o = [...c.sections_order]; [o[i], o[i + d]] = [o[i + d], o[i]]; setC({ ...c, sections_order: o }); setDirty(true); };
  const reorder = (o) => { setC({ ...c, sections_order: o }); setDirty(true); };
  const setBlock = (id) => (patch) => { setC((s) => ({ ...s, blocks: s.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)) })); setDirty(true); };
  const addBlock = (t) => { const b = newBlock(t); setC((s) => ({ ...s, blocks: [...s.blocks, b], sections_order: [...s.sections_order, b.id] })); setDirty(true); };
  const removeBlock = (id) => {
    if (!window.confirm("Bu bölüm taslaktan silinsin mi? (Yayındaki sayfa, yeniden yayınlanana kadar değişmez.)")) return;
    setC((s) => ({ ...s, blocks: s.blocks.filter((b) => b.id !== id), sections_order: s.sections_order.filter((k) => k !== id) })); setDirty(true);
  };
  const isHidden = (k) => (c.blocks.find((b) => b.id === k)?.visible === false) || (k !== "hero" && c[k]?.visible === false);
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); load(); return true; } catch (e) { toast.error(formatErr(e.response?.data?.detail)); return false; } };
  const save = () => call(() => api.put("/admin/cms/home/draft", c), "Taslak kaydedildi");
  const preview = async () => { if (dirty && !(await save())) return; window.open("/admin/ana-sayfa-onizleme", "_blank", "noopener"); };
  const publish = async () => {
    if (dirty && !(await save())) return;
    if (window.confirm("Taslak ana sayfada yayınlansın mı?")) call(() => api.post("/admin/cms/home/publish"), "Ana sayfa yayınlandı");
  };
  const rollback = (v) => window.confirm(`v${v} sürümü yeniden yayınlansın mı?`) && call(() => api.post(`/admin/cms/home/rollback/${v}`), `v${v} yayına alındı`);
  const reset = () => window.confirm("Ana sayfa varsayılan (CMS öncesi) içeriğe dönsün mü?") && call(() => api.post("/admin/cms/home/reset-default"), "Varsayılan içerik yayında");

  return (
    <div className="space-y-4" data-testid="cms-home-panel">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-bold">Ana Sayfa Yönetimi</h2>
          <p className="text-xs text-muted-foreground" data-testid="cms-status">
            Yayındaki: {meta.published ? `v${meta.published_version} · ${fmt(meta.published_at)}` : "varsayılan içerik"} · Taslak: {meta.draft ? fmt(meta.draft_updated_at) : "yok"}{dirty ? " · kaydedilmemiş değişiklik var" : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => { setC(DEFAULT_HOME); setDirty(true); }} data-testid="cms-load-default">Varsayılanı forma yükle</Button>
          <Button variant="outline" onClick={save} data-testid="cms-save"><Save className="w-4 h-4 mr-1" />Taslağı Kaydet</Button>
          <Button variant="outline" onClick={preview} data-testid="cms-preview"><Eye className="w-4 h-4 mr-1" />Önizle</Button>
          <Button onClick={publish} data-testid="cms-publish" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold"><Upload className="w-4 h-4 mr-1" />Yayınla</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        <div className="space-y-4">
          <HeroEditor h={c.hero} set={set("hero")} />
          <PillarsEditor p={c.pillars} set={set("pillars")} />
          <TitledEditor title="Hizmet kategorileri" k="categories" s={c.categories} set={set("categories")} />
          <TitledEditor title="Öne çıkan işletmeler" k="featured" s={c.featured} set={set("featured")} withMax />
          {c.blocks.map((b) => <BlockEditor key={b.id} b={b} set={setBlock(b.id)} onRemove={() => removeBlock(b.id)} />)}
          <AddBlockBar onAdd={addBlock} disabled={c.blocks.length >= 20} />
          <SeoEditor s={c.seo} set={set("seo")} />
        </div>
        <div className="space-y-4">
          <OrderList order={c.sections_order} onMove={move} onReorder={reorder} labelOf={(k) => sectionLabel(k, c.blocks)} isHidden={isHidden} />
          <AdStats />
          <Versions versions={versions} onRollback={rollback} />
          {meta.published && <Button variant="ghost" className="text-red-600" onClick={reset} data-testid="cms-reset-default">Varsayılan içeriğe dön</Button>}
        </div>
      </div>
    </div>
  );
};
